<script setup>
/**
 * @description 通用的基础 Banner 轮播组件,支持自动切换、拖拽、触摸滑动、鼠标滚轮等交互
 * @param {number} interval - 自动切换时间间隔(秒)
 * @param {boolean} carousel - 是否启用轮播
 * @param {string} defaultTheme - 默认主题色 (dark | light)
 * @param {string} height - Banner 高度
 * @param {boolean} showNav - 是否显示导航按钮
 * @param {boolean} showIndicators - 是否显示进度指示器
 * @param {boolean} pauseOnHover - 鼠标悬浮时是否暂停自动播放
 * @param {string} size - 组件尺寸 (default | small)
 * @emits change - 当前 Banner 索引改变时触发
 */

// 1. Imports
import { IconLeft, IconRight } from '@arco-design/web-vue/es/icon'
import { useMediaQuery } from '@/composables/useMediaQuery'
import { gsap } from 'gsap'
import { computed, nextTick, onMounted, onUnmounted, ref, watch, provide } from 'vue'

// 2. Props
const props = defineProps({
  interval: {
    type: Number,
    default: 3,
  },
  carousel: {
    type: Boolean,
    default: true,
  },
  defaultTheme: {
    type: String,
    default: 'dark',
  },
  height: {
    type: String,
    default: '536px',
  },
  showNav: {
    type: Boolean,
    default: true,
  },
  showIndicators: {
    type: Boolean,
    default: true,
  },
  pauseOnHover: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'small'].includes(value),
  },
  /**
   * @description 覆盖 touch-action CSS 属性
   * @reason iOS Safari 在 `touch-action: pan-y` 下会干扰 video 原生控件的交互，
   * 导致进度条拖拽后出现 seeking 响应错位问题。
   * 当某个 slide 内有 video 播放时，传入 'auto' 绕过此限制。
   */
  touchActionOverride: {
    type: String,
    default: '',
    validator: (value) =>
      ['', 'auto', 'pan-y', 'pan-x pan-y', 'none', 'manipulation'].includes(value),
  },
})

// 3. Emits
const emit = defineEmits(['change'])

// 4. Refs/Reactive State
const { isMobile } = useMediaQuery()
const currentIndex = ref(0)
const bannerContainerRef = ref(null)
const isHovering = ref(false)
const isDragging = ref(false)
const dragOffset = ref(0)
const dragThreshold = 50

const items = ref([])
const itemsCount = computed(() => items.value.length)
const isSingleItem = computed(() => itemsCount.value <= 1)

const touchStartX = ref(0)
const touchStartY = ref(0)
const touchCurrentX = ref(0)
const touchCurrentY = ref(0)
const touchEndX = ref(0)
const isScrolling = ref(false)
const clickPrevented = ref(false)
let mainTimeline = null
let progressTimeline = null
let autoPlayTimer = null
let ctx = null
let wheelEndTimer = null
let wheelRafId = null
let dragStartVideoEl = null

const CROSSFADE_DURATION = 0.5
const CROSSFADE_EASE = 'power1.out'

// 5. Provide context to children
const registerItem = (item) => {
  if (!items.value.find((i) => i.index === item.index)) {
    items.value.push(item)
    items.value.sort((a, b) => a.index - b.index)
  }
}

const unregisterItem = (item) => {
  const index = items.value.findIndex((i) => i.index === item.index)
  if (index > -1) items.value.splice(index, 1)
}

provide('baseBannerContext', {
  currentIndex,
  registerItem,
  unregisterItem,
  clickPrevented,
})

// 6. Methods
/**
 * @description 切换到指定幻灯片
 * @param {number} index - 目标索引
 * @param {boolean} [manual=false] - 是否为用户主动触发（手动导航时跳过 carousel 检查）
 */
const switchSlide = (index, manual = false) => {
  if (
    (!manual && !props.carousel) ||
    isSingleItem.value ||
    currentIndex.value === index ||
    itemsCount.value === 0
  )
    return

  if (mainTimeline) mainTimeline.kill()
  if (progressTimeline) progressTimeline.kill()
  if (autoPlayTimer) clearTimeout(autoPlayTimer)

  const oldIndex = currentIndex.value
  currentIndex.value = index
  emit('change', index)

  if (import.meta.client && bannerContainerRef.value) {
    const slides = bannerContainerRef.value.querySelectorAll('.base-banner-item')
    gsap.killTweensOf(slides)
    if (slides[oldIndex]) {
      gsap.set(slides[oldIndex], {
        opacity: 1,
        display: 'block',
        zIndex: 2,
        clearProps: 'transform,x,scale',
      })
    }
    if (slides[index]) {
      gsap.set(slides[index], {
        opacity: 0,
        display: 'block',
        zIndex: 1,
        clearProps: 'transform,x,scale',
      })
    }
    for (let i = 0; i < slides.length; i++) {
      if (i !== oldIndex && i !== index && slides[i]) {
        gsap.set(slides[i], {
          opacity: 0,
          display: 'block',
          zIndex: 0,
          clearProps: 'transform,x,scale',
        })
      }
    }
  }

  nextTick(() => {
    animateSlideChange()
  })
}

const nextSlide = () => {
  if (itemsCount.value === 0) return
  switchSlide((currentIndex.value + 1) % itemsCount.value, true)
}

const prevSlide = () => {
  if (itemsCount.value === 0) return
  switchSlide((currentIndex.value - 1 + itemsCount.value) % itemsCount.value, true)
}

const goToSlide = (index) => {
  if (isSingleItem.value || clickPrevented.value) {
    clickPrevented.value = false
    return
  }
  switchSlide(index, true)
}

const startAutoPlay = () => {
  if (!props.carousel || isSingleItem.value) return
  if (autoPlayTimer) clearTimeout(autoPlayTimer)
  if (progressTimeline) {
    progressTimeline.kill()
    progressTimeline = null
  }

  if (props.showIndicators) {
    startProgressAnimation()
  } else {
    autoPlayTimer = setTimeout(() => {
      if (props.carousel && !isSingleItem.value && itemsCount.value > 0) {
        nextSlide()
      }
    }, props.interval * 1000)
  }
}

const startProgressAnimation = () => {
  if (!props.showIndicators) return

  const tryStartAnimation = (attempts = 0) => {
    nextTick(() => {
      if (!import.meta.client || !bannerContainerRef.value) return

      const allProgressBars = bannerContainerRef.value.querySelectorAll('.indicator-item__progress')
      const count = itemsCount.value
      if (allProgressBars.length === 0 || count === 0) {
        if (attempts < 10) {
          setTimeout(() => tryStartAnimation(attempts + 1), 100)
        } else {
          autoPlayTimer = setTimeout(() => {
            if (props.carousel && !isSingleItem.value && count > 0) {
              nextSlide()
            }
          }, props.interval * 1000)
        }
        return
      }

      const idx = Math.min(currentIndex.value, allProgressBars.length - 1)
      const activeProgressBar = allProgressBars[idx]

      gsap.killTweensOf(allProgressBars)
      gsap.set(allProgressBars, { width: 0 })

      progressTimeline = gsap.to(activeProgressBar, {
        width: '100%',
        duration: props.interval,
        ease: 'none',
        onComplete: () => {
          if (props.carousel && !isSingleItem.value && count > 0) {
            nextSlide()
          }
        },
      })
    })
  }

  tryStartAnimation()
}

/**
 * @description 隐藏 slide，兼容 iOS 视频隐藏问题
 * @reason iOS Safari 存在 WebKit bug：对 `<video>` 所在容器设置 `display:none` 会
 * 触发连续 10-14 次 `seeking` 事件响应。
 * 因此对含 video 的 slide 使用 opacity:0 + pointer-events:none 替代 display:none，
 * 绕过此 bug。
 * @param {Element} slide
 */
const hideSlide = (slide) => {
  if (!slide) return
  if (slide.querySelector('video')) {
    gsap.set(slide, {
      opacity: 0,
      zIndex: 0,
      pointerEvents: 'none',
      display: 'block',
      clearProps: 'transform,x,scale',
    })
  } else {
    gsap.set(slide, {
      opacity: 0,
      zIndex: 0,
      display: 'none',
      clearProps: 'transform,x,scale',
    })
  }
}

const setInitialSlideState = () => {
  if (itemsCount.value === 0 || !import.meta.client || !bannerContainerRef.value) return
  const slides = bannerContainerRef.value.querySelectorAll('.base-banner-item')
  if (!slides.length) return
  gsap.killTweensOf(slides)
  const idx = currentIndex.value
  for (let i = 0; i < slides.length; i++) {
    if (i === idx) {
      gsap.set(slides[i], {
        opacity: 1,
        zIndex: 2,
        display: 'block',
        pointerEvents: '',
        clearProps: 'transform,x,scale',
      })
    } else {
      hideSlide(slides[i])
    }
  }
  if (props.carousel && !isSingleItem.value) startAutoPlay()
}

const animateSlideChange = () => {
  if (mainTimeline) mainTimeline.kill()
  if (itemsCount.value === 0) return

  startAutoPlay()

  if (!ctx && bannerContainerRef.value) {
    ctx = gsap.context(() => {}, bannerContainerRef.value)
  }

  if (ctx) {
    ctx.add((self) => {
      const selector = self.selector || gsap.utils.selector(bannerContainerRef.value || document)
      const activeSlideSelector = `.base-banner-item[data-index="${currentIndex.value}"]`
      const allSlides = selector('.base-banner-item')

      allSlides.forEach((slide) => {
        const slideIndex = parseInt(slide.getAttribute('data-index'))
        if (slideIndex === currentIndex.value) {
          gsap.set(slide, { zIndex: 2, display: 'block', opacity: 0, pointerEvents: '' })
        } else {
          gsap.set(slide, { zIndex: 1, display: 'block', pointerEvents: '' })
        }
      })

      const activeSlide = selector(activeSlideSelector)
      if (activeSlide.length) {
        gsap.set(activeSlide, {
          zIndex: 2,
          display: 'block',
          opacity: 0,
          pointerEvents: '',
          clearProps: 'transform,x,scale',
        })
        const tl = gsap.timeline({
          onComplete: () => {
            allSlides.forEach((s) => {
              const idx = parseInt(s.getAttribute('data-index'))
              if (idx !== currentIndex.value) {
                hideSlide(s)
              } else {
                gsap.set(s, { pointerEvents: '' })
              }
            })
          },
        })
        tl.to(
          activeSlide,
          {
            opacity: 1,
            duration: CROSSFADE_DURATION,
            ease: CROSSFADE_EASE,
          },
          0
        )

        allSlides.forEach((slide) => {
          const slideIndex = parseInt(slide.getAttribute('data-index'))
          if (slideIndex !== currentIndex.value) {
            tl.to(
              slide,
              {
                opacity: 0,
                duration: CROSSFADE_DURATION,
                ease: CROSSFADE_EASE,
              },
              0
            )
          }
        })

        const activeBg = selector(`${activeSlideSelector} .banner-bg`)
        if (activeBg.length) {
          gsap.set(activeBg, { scale: 1.03 })
          gsap.to(activeBg, {
            scale: 1,
            duration: CROSSFADE_DURATION,
            ease: CROSSFADE_EASE,
            overwrite: true,
          })
        }
      }

      mainTimeline = gsap.timeline({
        defaults: { ease: CROSSFADE_EASE, duration: 0.28 },
        delay: 0.12,
      })

      const contentSelector = `${activeSlideSelector} .banner-content__subtitle, ${activeSlideSelector} .banner-content__title, ${activeSlideSelector} .banner-content__desc, ${activeSlideSelector} .banner-content__actions`
      const infoElements = selector(contentSelector)
      if (infoElements.length) {
        gsap.set(infoElements, { opacity: 0 })
        const subtitle = selector(`${activeSlideSelector} .banner-content__subtitle`)
        const title = selector(`${activeSlideSelector} .banner-content__title`)
        const desc = selector(`${activeSlideSelector} .banner-content__desc`)
        const actions = selector(`${activeSlideSelector} .banner-content__actions`)
        if (subtitle.length)
          mainTimeline.fromTo(subtitle, { x: -16, opacity: 0 }, { x: 0, opacity: 1 })
        if (title.length)
          mainTimeline.fromTo(title, { y: 8, opacity: 0 }, { y: 0, opacity: 1 }, '-=0.18')
        if (desc.length)
          mainTimeline.fromTo(desc, { y: 8, opacity: 0 }, { y: 0, opacity: 0.9 }, '-=0.12')
        if (actions.length) mainTimeline.fromTo(actions, { opacity: 0 }, { opacity: 1 }, '-=0.08')
      }
    })
  }
}

const applyDragEffect = (isFinal = false, willSwitch = false) => {
  if (itemsCount.value === 0) return
  const container = bannerContainerRef.value
  if (!container) return
  const slides = container.querySelectorAll('.base-banner-item')
  if (!slides || slides.length === 0) return

  const currentIdx = currentIndex.value
  const nextIdx = (currentIdx + 1) % itemsCount.value
  const prevIdx = (currentIdx - 1 + itemsCount.value) % itemsCount.value
  const revealIndex = dragOffset.value > 0 ? prevIdx : nextIdx

  for (let i = 0; i < slides.length; i++) {
    if (i !== currentIdx && i !== revealIndex) {
      gsap.set(slides[i], { opacity: 0, zIndex: 0 })
    }
  }

  const containerWidth = container.offsetWidth || 1000
  const dragRatio = dragOffset.value / containerWidth
  const currentTargetX = dragRatio * 100
  const nextTargetX = (1 + dragRatio) * 100
  const prevTargetX = (dragRatio - 1) * 100

  if (slides[currentIdx]) {
    gsap.set(slides[currentIdx], {
      display: 'block',
      x: currentTargetX + '%',
      opacity: Math.max(0.15, 1 - Math.abs(dragRatio) * 2),
      scale: 1 - Math.abs(dragRatio) * 0.1,
      zIndex: 1,
    })
  }

  if (dragOffset.value > 0) {
    if (slides[prevIdx]) {
      gsap.set(slides[prevIdx], {
        display: 'block',
        x: prevTargetX + '%',
        opacity: Math.min(1, Math.abs(dragRatio) * 2),
        scale: 0.9 + Math.abs(dragRatio) * 0.1,
        zIndex: 1,
      })
    }
  } else {
    if (slides[nextIdx]) {
      gsap.set(slides[nextIdx], {
        display: 'block',
        x: nextTargetX + '%',
        opacity: Math.min(1, Math.abs(dragRatio) * 2),
        scale: 0.9 + Math.abs(dragRatio) * 0.1,
        zIndex: 1,
      })
    }
  }

  if (isFinal && !willSwitch) {
    animateSlideChange()
  }
}

const animateSlideChangeFromDrag = (oldIndex, newIndex, isDraggingRight) => {
  if (mainTimeline) mainTimeline.kill()
  if (progressTimeline) progressTimeline.kill()

  const container = bannerContainerRef.value
  if (!container || itemsCount.value === 0) return
  const slides = container.querySelectorAll('.base-banner-item')
  if (!slides.length || !slides[oldIndex] || !slides[newIndex]) return

  gsap.killTweensOf(slides)

  const oldTargetX = isDraggingRight ? '100%' : '-100%'

  for (let i = 0; i < slides.length; i++) {
    if (i !== oldIndex && i !== newIndex) {
      gsap.set(slides[i], { opacity: 0, zIndex: 0, display: 'block', pointerEvents: '' })
    }
  }
  gsap.set(slides[oldIndex], { opacity: 1, zIndex: 1, display: 'block', pointerEvents: '' })
  gsap.set(slides[newIndex], { opacity: 1, zIndex: 2, display: 'block', pointerEvents: '' })

  const duration = 0.32
  const ease = 'power1.out'

  const tl = gsap.timeline({
    onComplete: () => {
      hideSlide(slides[oldIndex])
      gsap.set(slides[newIndex], { clearProps: 'transform,x,scale', pointerEvents: '' })
      startAutoPlay()
    },
  })
  tl.to(slides[oldIndex], { x: oldTargetX, opacity: 0, scale: 0.9, duration, ease }, 0)
  tl.to(slides[newIndex], { x: '0%', opacity: 1, scale: 1, duration, ease }, 0)
}

/**
 * @description Prepare slides for dragging
 */
const prepareDrag = () => {
  if (progressTimeline) progressTimeline.kill()
  if (autoPlayTimer) clearTimeout(autoPlayTimer)

  const container = bannerContainerRef.value
  if (container && itemsCount.value >= 2) {
    const slides = container.querySelectorAll('.base-banner-item')
    const len = itemsCount.value
    const curr = currentIndex.value
    const nextIdx = (curr + 1) % len
    const prevIdx = (curr - 1 + len) % len
    if (slides[curr]) gsap.set(slides[curr], { display: 'block' })
    if (slides[nextIdx])
      gsap.set(slides[nextIdx], { display: 'block', x: '100%', opacity: 0, zIndex: 1 })
    if (slides[prevIdx])
      gsap.set(slides[prevIdx], { display: 'block', x: '-100%', opacity: 0, zIndex: 1 })
  }
}

const handleDragStart = (startX, startY = 0) => {
  touchStartX.value = startX
  touchStartY.value = startY
  if (!props.carousel || isSingleItem.value) return
  touchCurrentX.value = startX
  touchCurrentY.value = startY
  dragOffset.value = 0
  isScrolling.value = false
}

/**
 * @description iOS 兼容：忽略原生 video 控件的触摸开始
 * @reason iOS Safari 在 touch-action: pan-y 模式下会干扰 video 控件的交互，
 * 需要在 video controls 区域触摸时跳过 Banner 拖拽逻辑，
 * 防止进度条拖拽被 Banner 的 touchMoveHandler 的 preventDefault 拦截。
 * @param {TouchEvent} event
 */
const handleBannerTouchStart = (event) => {
  const touch = event.touches[0]
  const videoEl = event.target?.closest?.('video')
  if (videoEl?.controls) {
    touchStartX.value = touch.clientX
    touchStartY.value = touch.clientY
    return
  }
  handleDragStart(touch.clientX, touch.clientY)
}

/**
 * @description iOS 兼容：忽略原生 video 控件的触摸结束
 * @param {TouchEvent} event
 */
const handleBannerTouchEnd = (event) => {
  const videoEl = event.target?.closest?.('video')
  if (videoEl?.controls) return
  handleDragEnd(event.changedTouches[0].clientX)
}

const handleDragMove = (currentX) => {
  if (!isDragging.value) return
  touchCurrentX.value = currentX
  dragOffset.value = currentX - touchStartX.value
  applyDragEffect()
}

const handleDragEnd = (endX) => {
  if (!isDragging.value) {
    isScrolling.value = false
    if (isSingleItem.value) {
      const diffX = Math.abs(endX - touchStartX.value)
      if (diffX >= 8) {
        clickPrevented.value = true
        setTimeout(() => {
          clickPrevented.value = false
        }, 50)
      }
    }
    return
  }
  touchEndX.value = endX
  isDragging.value = false
  isScrolling.value = false
  const diffX = Math.abs(touchEndX.value - touchStartX.value)
  const willSwitch = diffX >= dragThreshold

  if (willSwitch) {
    const oldIndex = currentIndex.value
    const isDraggingRight = dragOffset.value > 0
    const newIndex = isDraggingRight
      ? (oldIndex - 1 + itemsCount.value) % itemsCount.value
      : (oldIndex + 1) % itemsCount.value
    currentIndex.value = newIndex
    emit('change', newIndex)
    clickPrevented.value = true
    animateSlideChangeFromDrag(oldIndex, newIndex, isDraggingRight)
    setTimeout(() => {
      clickPrevented.value = false
    }, 50)
  } else {
    applyDragEffect(true, false)
    startAutoPlay()
  }
}

const handleWheel = (event) => {
  if (!props.carousel || isSingleItem.value || itemsCount.value === 0) return
  const deltaX = event.deltaX
  if (Math.abs(deltaX) < 0.5) return
  if (import.meta.client) event.preventDefault()

  if (!isDragging.value) {
    isDragging.value = true
    touchStartX.value = 0
    touchCurrentX.value = 0
    dragOffset.value = 0
    if (progressTimeline) progressTimeline.kill()
    if (autoPlayTimer) clearTimeout(autoPlayTimer)
  }

  touchCurrentX.value -= deltaX
  dragOffset.value = touchCurrentX.value - touchStartX.value

  if (wheelRafId == null) {
    wheelRafId = requestAnimationFrame(() => {
      wheelRafId = null
      applyDragEffect()
    })
  }

  if (wheelEndTimer) clearTimeout(wheelEndTimer)
  wheelEndTimer = setTimeout(handleWheelEnd, 50)
}

const handleWheelEnd = () => {
  wheelEndTimer = null
  if (!isDragging.value) return
  if (wheelRafId != null) {
    cancelAnimationFrame(wheelRafId)
    wheelRafId = null
    applyDragEffect()
  }
  handleDragEnd(touchCurrentX.value)
}

const handleSingleItemMouseUp = (event) => {
  document.removeEventListener('mouseup', handleSingleItemMouseUp)
  document.removeEventListener('mouseleave', handleSingleItemMouseUp)
  const diffX = Math.abs(event.clientX - touchStartX.value)
  if (diffX >= 8) {
    clickPrevented.value = true
    setTimeout(() => {
      clickPrevented.value = false
    }, 50)
  }
}

const handleMouseDown = (event) => {
  if (event.button !== 0) return
  if (event.target?.closest?.('.base-banner__nav')) return

  const videoEl = event.target?.closest?.('video')
  const isVideoWithControls = !!videoEl?.controls

  if (!isVideoWithControls) {
    event.preventDefault()
  }

  if (!props.carousel || isSingleItem.value) {
    touchStartX.value = event.clientX
    document.addEventListener('mouseup', handleSingleItemMouseUp)
    document.addEventListener('mouseleave', handleSingleItemMouseUp)
    return
  }

  handleDragStart(event.clientX, event.clientY)

  if (isVideoWithControls) {
    dragStartVideoEl = videoEl
  } else {
    isDragging.value = true
    prepareDrag()
  }

  document.addEventListener('mousemove', handleGlobalMouseMove)
  document.addEventListener('mouseup', handleGlobalMouseUp)
  document.addEventListener('mouseleave', handleGlobalMouseUp)
}

const handleGlobalMouseMove = (event) => {
  if (!isDragging.value && dragStartVideoEl !== null) {
    const diffX = Math.abs(event.clientX - touchStartX.value)
    if (diffX < 8) return
    isDragging.value = true
    prepareDrag()
    dragStartVideoEl.addEventListener(
      'click',
      (e) => {
        e.preventDefault()
        e.stopPropagation()
      },
      { capture: true, once: true }
    )
    dragStartVideoEl = null
  }
  handleDragMove(event.clientX)
}

const handleGlobalMouseUp = (event) => {
  document.removeEventListener('mousemove', handleGlobalMouseMove)
  document.removeEventListener('mouseup', handleGlobalMouseUp)
  document.removeEventListener('mouseleave', handleGlobalMouseUp)
  if (!isDragging.value) {
    dragStartVideoEl = null
    return
  }
  handleDragEnd(event.clientX)
  dragStartVideoEl = null
}

const handleTouchCancel = () => {
  if (!isDragging.value) {
    isScrolling.value = false
    return
  }
  isDragging.value = false
  isScrolling.value = false
  applyDragEffect(true)
  startAutoPlay()
}

const touchMoveHandler = (e) => {
  if (!props.carousel || isSingleItem.value) return
  const videoEl = e.target?.closest?.('video')
  if (videoEl?.controls) return

  const currentX = e.touches[0].clientX
  const currentY = e.touches[0].clientY

  if (!isDragging.value && !isScrolling.value) {
    const diffX = Math.abs(currentX - touchStartX.value)
    const diffY = Math.abs(currentY - touchStartY.value)

    if (diffX > 10 || diffY > 10) {
      if (diffX > diffY) {
        isDragging.value = true
        prepareDrag()
      } else {
        isScrolling.value = true
      }
    }
  }

  if (isDragging.value) {
    if (e.cancelable) e.preventDefault()
    handleDragMove(currentX)
  }
}

const wheelHandler = (e) => {
  if (e.deltaX !== 0) handleWheel(e)
}

// 7. Lifecycle
onMounted(async () => {
  await nextTick()
  if (bannerContainerRef.value) {
    ctx = gsap.context(() => {}, bannerContainerRef.value)
    bannerContainerRef.value.addEventListener('touchmove', touchMoveHandler, { passive: false })
    bannerContainerRef.value.addEventListener('wheel', wheelHandler, { passive: false })
  }

  if (itemsCount.value > 0) {
    setInitialSlideState()
  }
})

onUnmounted(() => {
  if (wheelEndTimer) clearTimeout(wheelEndTimer)
  if (wheelRafId != null) cancelAnimationFrame(wheelRafId)
  if (autoPlayTimer) clearTimeout(autoPlayTimer)
  if (mainTimeline) mainTimeline.kill()
  if (progressTimeline) progressTimeline.kill()
  if (ctx) {
    ctx.revert()
    ctx.kill()
  }
  if (bannerContainerRef.value) {
    bannerContainerRef.value.removeEventListener('touchmove', touchMoveHandler)
    bannerContainerRef.value.removeEventListener('wheel', wheelHandler)
  }
})

watch(
  () => items.value.length,
  (newVal) => {
    if (newVal > 0) {
      currentIndex.value = 0
      emit('change', 0)
      nextTick(() => {
        setInitialSlideState()
      })
    }
  }
)

watch(isHovering, (hovering) => {
  if (!props.pauseOnHover || !props.carousel || isSingleItem.value) return

  if (hovering) {
    if (progressTimeline) {
      progressTimeline.pause()
    }
    if (autoPlayTimer) {
      clearTimeout(autoPlayTimer)
      autoPlayTimer = null
    }
  } else {
    if (progressTimeline) {
      progressTimeline.resume()
    } else {
      startAutoPlay()
    }
  }
})

watch(
  () => props.carousel,
  (newVal, oldVal) => {
    if (newVal && !oldVal && !isSingleItem.value && itemsCount.value > 0) {
      nextTick(() => {
        startAutoPlay()
      })
    } else if (!newVal && oldVal) {
      if (progressTimeline) {
        progressTimeline.kill()
        progressTimeline = null
      }
      if (autoPlayTimer) {
        clearTimeout(autoPlayTimer)
        autoPlayTimer = null
      }
    }
  }
)
</script>

<template>
  <div
    ref="bannerContainerRef"
    class="base-banner"
    :class="{
      'base-banner--no-carousel': !carousel,
      'base-banner--small': size === 'small',
    }"
    :style="{
      height: height,
      ...(touchActionOverride ? { touchAction: touchActionOverride } : {}),
    }"
    @mouseenter="isHovering = true"
    @mouseleave="isHovering = false"
    @mousedown="carousel ? handleMouseDown($event) : null"
    @dragstart.prevent
    @touchstart="carousel ? handleBannerTouchStart($event) : null"
    @touchend="carousel ? handleBannerTouchEnd($event) : null"
    @touchcancel="carousel ? handleTouchCancel() : null"
  >
    <div class="base-banner__slides">
      <slot />
    </div>

    <div
      v-if="showNav && !isSingleItem && !isMobile"
      class="base-banner__nav"
      :class="{ 'base-banner__nav--small': size === 'small' }"
    >
      <button
        :class="['nav-btn nav-btn--left', { 'nav-btn--visible': isHovering }]"
        type="button"
        aria-label="上一张"
        @click.stop="prevSlide"
      >
        <icon-left />
      </button>
      <button
        :class="['nav-btn nav-btn--right', { 'nav-btn--visible': isHovering }]"
        type="button"
        aria-label="下一张"
        @click.stop="nextSlide"
      >
        <icon-right />
      </button>
    </div>

    <div
      v-if="showIndicators && !isSingleItem"
      class="base-banner__indicators"
      :class="[
        `base-banner__indicators--theme-${items[currentIndex]?.theme || defaultTheme}`,
        { 'base-banner__indicators--small': size === 'small' },
      ]"
    >
      <div
        v-for="(_, index) in items"
        :key="index"
        class="indicator-item"
        @click.stop="goToSlide(index)"
      >
        <div
          class="indicator-item__progress"
          :class="{ 'indicator-item__progress--active': index === currentIndex }"
        ></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.base-banner {
  width: 100%;
  position: relative;
  touch-action: pan-y;
  user-select: none;
  cursor: grab;
  overflow: hidden;

  :deep(img),
  :deep(video) {
    -webkit-user-drag: none;
    user-drag: none;
    pointer-events: none;
  }

  :deep(video[controls]) {
    pointer-events: auto;
  }

  @include respond-to-max('md') {
    height: 548px;
  }

  &:active {
    cursor: grabbing;
  }

  &--no-carousel {
    cursor: default;
    &:active {
      cursor: default;
    }
  }

  &__slides {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    contain: layout style paint;
  }

  &__nav {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 64px;
    z-index: 10;
    pointer-events: none;

    > * {
      pointer-events: auto;
    }

    &--small {
      padding: 0 16px;
    }
  }

  .nav-btn {
    width: $base-carousel-nav-button-size;
    height: $base-carousel-nav-button-size;
    border-radius: 50%;
    background-color: $base-carousel-nav-button-bg;
    color: $bg-white;
    backdrop-filter: blur($base-carousel-nav-button-backdrop-blur);
    border: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity $base-carousel-nav-button-transition-duration
        $base-carousel-nav-button-transition-easing,
      visibility $base-carousel-nav-button-transition-duration
        $base-carousel-nav-button-transition-easing,
      background-color $base-carousel-nav-button-transition-duration
        $base-carousel-nav-button-transition-easing;
    box-shadow: none;

    &--visible {
      opacity: 1;
      visibility: visible;
    }

    :deep(.arco-icon) {
      font-size: $base-carousel-nav-button-icon-size;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    &--left {
      :deep(.arco-icon) {
        transform: translateX(-$base-carousel-nav-button-icon-optical-offset);
      }
    }

    &--right {
      :deep(.arco-icon) {
        transform: translateX($base-carousel-nav-button-icon-optical-offset);
      }
    }

    &:hover:not(:disabled) {
      background-color: $base-carousel-nav-button-bg-hover;
      border-color: transparent;
      color: $bg-white;
      box-shadow: none;
    }

    &:active:not(:disabled) {
      background-color: $base-carousel-nav-button-bg-hover;
    }
  }

  &__indicators {
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 10px;
    z-index: 10;

    .indicator-item {
      width: 160px;
      height: 2px;
      background-color: rgba($bg-white, 0.3);
      cursor: pointer;
      position: relative;
      overflow: hidden;

      &__progress {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 0;
        background-color: $bg-white;
      }

      &:hover {
        background-color: rgba($bg-white, 0.5);
      }

      @include respond-to-max('lg') {
        width: 100px;
      }

      @include respond-to-max('md') {
        width: 40px;
      }
    }

    &--small {
      bottom: 16px;
      gap: 8px;

      .indicator-item {
        width: 8px;
        height: 8px;
        border-radius: 50%;

        &__progress {
          border-radius: 50%;
        }

        @include respond-to-max('md') {
          width: 6px;
          height: 6px;
        }
      }
    }
  }
}
</style>
